import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(d);
}

export function formatDateShort(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(d);
}

export function formatPhone(phone: string | undefined | null): string {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function validatePhone(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10;
}

export function generateVerificationToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

export function metersToMiles(meters: number): number {
  return meters * 0.000621371;
}

export function secondsToMinutes(seconds: number): number {
  return Math.ceil(seconds / 60);
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getZipcodeFromAddress(address: string): string | null {
  const match = address.match(/\b\d{5}(?:-\d{4})?\b/);
  return match ? match[0] : null;
}

// Summer camp sessions for 2026
export const SUMMER_2026_SESSIONS = [
  { id: 'week1', name: 'Week 1', startDate: '2026-06-08', endDate: '2026-06-12', dates: 'Jun 8 - Jun 12' },
  { id: 'week2', name: 'Week 2', startDate: '2026-06-15', endDate: '2026-06-18', dates: 'Jun 15 - Jun 18' },
  { id: 'week3', name: 'Week 3', startDate: '2026-06-22', endDate: '2026-06-26', dates: 'Jun 22 - Jun 26' },
  { id: 'week4', name: 'Week 4', startDate: '2026-06-29', endDate: '2026-07-02', dates: 'Jun 29 - Jul 2' },
  { id: 'week5', name: 'Week 5', startDate: '2026-07-06', endDate: '2026-07-10', dates: 'Jul 6 - Jul 10' },
  { id: 'week6', name: 'Week 6', startDate: '2026-07-13', endDate: '2026-07-17', dates: 'Jul 13 - Jul 17' },
  { id: 'week7', name: 'Week 7', startDate: '2026-07-20', endDate: '2026-07-24', dates: 'Jul 20 - Jul 24' },
  { id: 'week8', name: 'Week 8', startDate: '2026-07-27', endDate: '2026-07-31', dates: 'Jul 27 - Jul 31' },
  { id: 'week9', name: 'Week 9', startDate: '2026-08-03', endDate: '2026-08-07', dates: 'Aug 3 - Aug 7' },
  { id: 'week10', name: 'Week 10', startDate: '2026-08-10', endDate: '2026-08-14', dates: 'Aug 10 - Aug 14' },
];

// Hubstop configurations with pricing and zip codes
export const HUBSTOPS = [
  {
    id: 'passyunk-square',
    name: 'Passyunk Square',
    pricePerWeek: 165,
    isCenterCity: true,
    zipCodes: ['19147', '19148'],
    address: 'Passyunk Ave & 11th St, Philadelphia, PA',
    meetingTime: 'Estimated 7:15 AM',
    departureTime: '3:45-4 PM',
  },
  {
    id: 'graduate-hospital',
    name: 'Graduate Hospital',
    pricePerWeek: 165,
    isCenterCity: true,
    zipCodes: ['19146'],
    address: '23rd & South St, Philadelphia, PA',
    meetingTime: 'Estimated 7:20 AM',
    departureTime: '3:45-4 PM',
  },
  {
    id: 'rittenhouse',
    name: 'Rittenhouse Square',
    pricePerWeek: 165,
    isCenterCity: true,
    zipCodes: ['19103', '19102', '19146'],
    address: 'Rittenhouse Square, Philadelphia, PA',
    meetingTime: 'Estimated 7:25 AM',
    departureTime: '3:45-4 PM',
  },
  {
    id: 'washington-square',
    name: 'Washington Square',
    pricePerWeek: 165,
    isCenterCity: true,
    zipCodes: ['19106', '19107', '19102'],
    address: 'Washington Square, Philadelphia, PA',
    meetingTime: 'Estimated 7:20 AM',
    departureTime: '3:45-4 PM',
  },
  {
    id: 'fairmount',
    name: 'Fairmount',
    pricePerWeek: 165,
    isCenterCity: true,
    zipCodes: ['19130', '19121', '19123', '19131'],
    address: 'Fairmount & 22nd St, Philadelphia, PA',
    meetingTime: 'Estimated 7:30 AM',
    departureTime: '3:45-4 PM',
  },
  {
    id: 'northern-liberties',
    name: 'Northern Liberties',
    pricePerWeek: 165,
    isCenterCity: true,
    zipCodes: ['19123', '19125', '19122'],
    address: '2nd & Poplar St, Philadelphia, PA',
    meetingTime: 'Estimated 7:15 AM',
    departureTime: '3:45-4 PM',
  },
  {
    id: 'queen-village-bella-vista',
    name: 'Queen Village/Bella Vista',
    pricePerWeek: 165,
    isCenterCity: true,
    zipCodes: ['19147', '19148', '19106', '19107'],
    address: 'South St & 4th St, Philadelphia, PA',
    meetingTime: 'Estimated 7:10 AM',
    departureTime: '3:45-4 PM',
  },
  {
    id: 'manayunk',
    name: 'Manayunk',
    pricePerWeek: 75,
    isCenterCity: false,
    zipCodes: ['19127'],
    address: 'Main St & Shurs Ln, Philadelphia, PA',
    meetingTime: 'Estimated 8:30 AM',
    departureTime: '3:30 PM',
  },
  {
    id: 'roxborough',
    name: 'Roxborough',
    pricePerWeek: 75,
    isCenterCity: false,
    zipCodes: ['19128'],
    address: 'Ridge Ave & Roxborough Ave, Philadelphia, PA',
    meetingTime: 'Estimated 8:30 AM',
    departureTime: '3:30 PM',
  },
  {
    id: 'east-falls',
    name: 'East Falls',
    pricePerWeek: 75,
    isCenterCity: false,
    zipCodes: ['19129'],
    address: 'Ridge Ave & Midvale Ave, Philadelphia, PA',
    meetingTime: 'Estimated 8:30 AM',
    departureTime: '3:30 PM',
  },
  {
    id: 'gladwyne',
    name: 'Gladwyne',
    pricePerWeek: 165,
    isCenterCity: false,
    zipCodes: ['19035'],
    address: 'Youngs Ford Rd & Righters Mill Rd, Gladwyne, PA',
    meetingTime: 'Estimated 7:30 AM',
    departureTime: '3:45-4 PM',
  },
  {
    id: 'chestnut-hill',
    name: 'Chestnut Hill',
    pricePerWeek: 75,
    isCenterCity: false,
    zipCodes: ['19118', '19150', '19119'],
    address: 'Germantown Ave & Willow Grove Ave, Philadelphia, PA',
    meetingTime: 'Estimated 7:55 AM',
    departureTime: '3:45-4 PM',
  },
];

// Get hubstop by ID
export function getHubstopById(id: string) {
  return HUBSTOPS.find(h => h.id === id);
}

// Get hubstop by zipcode
export function getHubstopsByZipcode(zipcode: string) {
  return HUBSTOPS.filter(h => h.zipCodes.includes(zipcode));
}
